import { useNavigate } from 'react-router-dom';
import { Users, Search, FileText, Tag, Scan, BarChart, LogOut, User, Upload, Trash2, UserCog, Calendar, Mail, Send, Clock, MessageCircle, BookOpen, Inbox, DollarSign, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';

const Dashboard = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('');

  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    const storedRole = localStorage.getItem('role');
    setUsername(storedUsername || '');
    setRole(storedRole || '');
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('username');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const memberFeatures = [
    {
      title: 'Register Member',
      description: 'Add new members to the database',
      icon: <Users size={40} />,
      path: '/register',
      color: '#1e40af'
    },
    {
      title: 'Search Member',
      description: 'Find members by various criteria',
      icon: <Search size={40} />,
      path: '/search',
      color: '#3b82f6'
    },
    {
      title: 'Member Reports',
      description: 'View detailed member reports',
      icon: <FileText size={40} />,
      path: '/reports',
      color: '#60a5fa'
    },
    {
      title: 'Print Member Label',
      description: 'Generate and print member labels',
      icon: <Tag size={40} />,
      path: '/print-label',
      color: '#2563eb'
    }
  ];

  const advertiserFeatures = [
    {
      title: 'Register Advertiser',
      description: 'Add new advertisers to the database',
      icon: <Users size={40} />,
      path: '/register-advertiser',
      color: '#059669'
    },
    {
      title: 'Search Advertiser',
      description: 'Find advertisers by various criteria',
      icon: <Search size={40} />,
      path: '/search-advertiser',
      color: '#10b981'
    },
    {
      title: 'Advertiser Reports',
      description: 'View detailed advertiser reports',
      icon: <FileText size={40} />,
      path: '/advertiser-reports',
      color: '#34d399'
    },
    {
      title: 'Print Advertiser Label',
      description: 'Generate and print advertiser labels',
      icon: <Tag size={40} />,
      path: '/print-advertiser-label',
      color: '#6ee7b7'
    }
  ];

  const commonFeatures = [
    {
      title: 'Scan Dispatch',
      description: 'Scan QR codes and manage dispatches',
      icon: <Scan size={40} />,
      path: '/scan-dispatch',
      color: '#1d4ed8'
    },
    {
      title: 'Scan Dispatch Mobile',
      description: 'Scan using mobile camera (QR & Barcode)',
      icon: <Scan size={40} />,
      path: '/scan-dispatch-mobile',
      color: '#059669'
    },
    {
      title: 'Mark Failed Courier',
      description: 'Mark returned parcels as failed',
      icon: <Scan size={40} />,
      path: '/mark-failed-courier',
      color: '#dc2626'
    },
    {
      title: 'Dispatch Reports',
      description: 'View all dispatch records',
      icon: <BarChart size={40} />,
      path: '/dispatch-reports',
      color: '#1e3a8a'
    },
    {
      title: 'Courier Export',
      description: 'Scan IDs and download Excel for courier partner',
      icon: <Package size={40} />,
      path: '/courier-export',
      color: '#0891b2'
    }
  ];

  const accountingFeatures = [
    {
      title: 'Enter Bill',
      description: 'Create bills and invoices',
      icon: <FileText size={40} />,
      path: '/enter-bill',
      color: '#7c3aed'
    },
    {
      title: 'Bill Reports',
      description: 'View and edit all bills',
      icon: <BarChart size={40} />,
      path: '/bill-reports',
      color: '#8b5cf6'
    },
    {
      title: 'Print Bill',
      description: 'Search and print bills',
      icon: <FileText size={40} />,
      path: '/print-bill',
      color: '#9333ea'
    },
    {
      title: 'Create Proforma Invoice',
      description: 'Create new Proforma Invoices',
      icon: <FileText size={40} />,
      path: '/create-proforma-invoice',
      color: '#059669'
    },
    {
      title: 'PI Reports',
      description: 'View Proforma Invoice reports',
      icon: <BarChart size={40} />,
      path: '/proforma-invoice-reports',
      color: '#10b981'
    }
  ];

  const emailFeatures = [
    {
      title: 'Email Settings',
      description: 'Configure Brevo API keys for both magazines',
      icon: <UserCog size={40} />,
      path: '/email-settings',
      color: '#7c3aed'
    },
    {
      title: 'Renewal Reminders',
      description: 'Send subscription renewal emails to members',
      icon: <Mail size={40} />,
      path: '/renewal-reminders',
      color: '#059669'
    },
    {
      title: 'Monthly Magazine',
      description: 'Send monthly magazine to members & advertisers',
      icon: <FileText size={40} />,
      path: '/monthly-magazine-email',
      color: '#2563eb'
    },
    {
      title: 'Ad Proposal',
      description: 'Create and send advertisement proposals',
      icon: <Send size={40} />,
      path: '/advertisement-proposal',
      color: '#dc2626'
    },
    {
      title: 'Ad Proposal Reports',
      description: 'View sent proposals & send follow-ups',
      icon: <BarChart size={40} />,
      path: '/ad-proposal-reports',
      color: '#ea580c'
    },
    {
      title: 'Proposal Drafts',
      description: 'View and edit saved proposal drafts',
      icon: <FileText size={40} />,
      path: '/proposal-drafts',
      color: '#6366f1'
    },
    {
      title: 'Greetings Email',
      description: 'Send festival greetings to members & advertisers',
      icon: <Mail size={40} />,
      path: '/greetings-email',
      color: '#f59e0b'
    },
    {
      title: 'News Broadcast',
      description: 'Share news articles with your audience',
      icon: <FileText size={40} />,
      path: '/news-broadcast',
      color: '#0ea5e9'
    },
    {
      title: 'WhatsApp Broadcast',
      description: 'Send WhatsApp messages to members',
      icon: <MessageCircle size={40} />,
      path: '/whatsapp-broadcast',
      color: '#25D366'
    },
    {
      title: 'WhatsApp Template Send',
      description: 'Send magazine via pre-approved templates',
      icon: <BookOpen size={40} />,
      path: '/whatsapp-template-send',
      color: '#128C7E'
    },
    {
      title: 'WhatsApp Magazine (Old)',
      description: 'Legacy magazine broadcast',
      icon: <BookOpen size={40} />,
      path: '/whatsapp-magazine-broadcast',
      color: '#6b7280'
    },
    {
      title: 'WhatsApp Opt-in',
      description: 'Send subscription request to all users',
      icon: <MessageCircle size={40} />,
      path: '/whatsapp-optin',
      color: '#075E54'
    },
    {
      title: 'WhatsApp Inbox',
      description: 'View incoming messages & replies',
      icon: <Inbox size={40} />,
      path: '/whatsapp-inbox',
      color: '#128C7E'
    },
    {
      title: 'WhatsApp Reports',
      description: 'View campaign reports & failed numbers',
      icon: <BarChart size={40} />,
      path: '/whatsapp-reports',
      color: '#075E54'
    },
    {
      title: 'Email Reports',
      description: 'View all sent emails and their status',
      icon: <BarChart size={40} />,
      path: '/email-reports',
      color: '#8b5cf6'
    }
  ];

  const adminFeatures = [
    {
      title: 'Import Data',
      description: 'Import members, advertisers, and bills from Excel',
      icon: <Upload size={40} />,
      path: '/import-data',
      color: '#dc2626'
    },
    {
      title: 'Export All Data',
      description: 'Export complete database tables for backup',
      icon: <FileText size={40} />,
      path: '/export-data',
      color: '#dc2626'
    },
    {
      title: 'Clear Data',
      description: 'Selectively clear database tables',
      icon: <Trash2 size={40} />,
      path: '/clear-data',
      color: '#dc2626'
    },
    {
      title: 'Manage Users',
      description: 'Add, edit, or delete user accounts',
      icon: <UserCog size={40} />,
      path: '/manage-users',
      color: '#4f46e5'
    },
    {
      title: 'Session Logs',
      description: 'View user login sessions and history',
      icon: <Clock size={40} />,
      path: '/session-logs',
      color: '#059669'
    },
    {
      title: 'Migrate Dates',
      description: 'Convert expiry dates from mm/dd/yyyy to dd/mm/yyyy',
      icon: <Calendar size={40} />,
      path: '/migrate-dates',
      color: '#2563eb'
    },
    {
      title: 'Tariff Management',
      description: 'Manage subscription prices and plan rates',
      icon: <DollarSign size={40} />,
      path: '/tariff-management',
      color: '#7c3aed'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <header className="dashboard-nav px-6 py-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold" style={{ color: 'var(--primary-blue)' }} data-testid="dashboard-title">
              Member Management System
            </h1>
            <p className="text-gray-600 mt-2 text-lg">Manage your members and dispatches efficiently</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right mr-4">
              <div className="flex items-center space-x-2 text-gray-700">
                <User size={18} />
                <span className="font-semibold">{username}</span>
              </div>
              <span className="text-sm text-gray-500 capitalize">{role}</span>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center space-x-2"
              data-testid="logout-button"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Member Module */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--primary-blue)' }}>
            Member Management
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {memberFeatures.map((feature, index) => (
              <div
                key={index}
                className="feature-card"
                onClick={() => navigate(feature.path)}
                data-testid={`feature-card-${feature.title.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div
                    className="p-4 rounded-full"
                    style={{ backgroundColor: `${feature.color}15`, color: feature.color }}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                  <Button
                    className="mt-4"
                    style={{ backgroundColor: feature.color }}
                    data-testid={`navigate-${feature.title.toLowerCase().replace(' ', '-')}`}
                  >
                    Open
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Advertiser Module */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#059669' }}>
            Advertiser Management
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advertiserFeatures.map((feature, index) => (
              <div
                key={index}
                className="feature-card"
                onClick={() => navigate(feature.path)}
                data-testid={`feature-card-${feature.title.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div
                    className="p-4 rounded-full"
                    style={{ backgroundColor: `${feature.color}15`, color: feature.color }}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                  <Button
                    className="mt-4"
                    style={{ backgroundColor: feature.color }}
                    data-testid={`navigate-${feature.title.toLowerCase().replace(' ', '-')}`}
                  >
                    Open
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Common Features */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6" style={{ color: 'var(--primary-blue)' }}>
            Dispatch Management
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {commonFeatures.map((feature, index) => (
              <div
                key={index}
                className="feature-card"
                onClick={() => navigate(feature.path)}
                data-testid={`feature-card-${feature.title.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div
                    className="p-4 rounded-full"
                    style={{ backgroundColor: `${feature.color}15`, color: feature.color }}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                  <Button
                    className="mt-4"
                    style={{ backgroundColor: feature.color }}
                    data-testid={`navigate-${feature.title.toLowerCase().replace(' ', '-')}`}
                  >
                    Open
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Email Campaigns Module */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#7c3aed' }}>
            📧 Email Campaigns
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {emailFeatures.map((feature, index) => (
              <div
                key={index}
                className="feature-card"
                onClick={() => navigate(feature.path)}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div
                    className="p-4 rounded-full"
                    style={{ backgroundColor: `${feature.color}15`, color: feature.color }}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                  <Button
                    className="mt-4"
                    style={{ backgroundColor: feature.color }}
                  >
                    Open
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Accounting Section */}
        <div>
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#7c3aed' }}>
            Accounting & Billing
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {accountingFeatures.map((feature, index) => (
              <div
                key={index}
                className="feature-card"
                onClick={() => navigate(feature.path)}
                data-testid={`feature-card-${feature.title.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div
                    className="p-4 rounded-full"
                    style={{ backgroundColor: `${feature.color}15`, color: feature.color }}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                  <Button
                    className="mt-4"
                    style={{ backgroundColor: feature.color }}
                    data-testid={`navigate-${feature.title.toLowerCase().replace(' ', '-')}`}
                  >
                    Open
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Admin Section */}
        {role === 'admin' && (
          <div className="mt-12">
            <h2 className="text-3xl font-bold mb-6" style={{ color: '#dc2626' }}>
              Admin Tools
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {adminFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="feature-card"
                  onClick={() => navigate(feature.path)}
                  data-testid={`feature-card-${feature.title.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div
                      className="p-4 rounded-full"
                      style={{ backgroundColor: `${feature.color}15`, color: feature.color }}
                    >
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                    <Button
                      className="mt-4"
                      style={{ backgroundColor: feature.color }}
                      data-testid={`navigate-${feature.title.toLowerCase().replace(' ', '-')}`}
                    >
                      Open
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;